import React, { useCallback, useMemo } from "react";
import copy from "copy-to-clipboard";
import ClipboardIcon from "../assets/clipboard_icon_primary.svg";
import LineIcon from "../assets/line_icon.png";
import { Button, showToast } from "@twioku/common_components";
import { useLineLink } from "../features/lineLink/hooks/useLineLink";
import { useNewAnswererInfo } from "../features/newAnswererInfo/hooks/useNewAnswererInfo";
import { useDeviceType } from "../utils/hooks/useDeviceType";

type Props = {
    isError: boolean;
    onActionClicked?: () => void;
    lineLinkUrl?: string;
};

export const LineLinkShare: React.FC<Props> = ({
    isError,
    onActionClicked,
    lineLinkUrl,
}) => {
    const deviceType = useDeviceType();
    const isMobile = deviceType === "mobile";

    const { newAnswererInfo, form } = useNewAnswererInfo();
    const { shareLineLink } = useLineLink();
    const watchedReceiverNickname = form.watch("receiverNickname");

    const invitationMessage = useMemo(() => {
        if (!watchedReceiverNickname || !lineLinkUrl) {
            return "";
        }
        return `${watchedReceiverNickname}です。

「ツイオク」アプリへの招待です。

下記リンクをタップして、ご利用を開始してください。

${lineLinkUrl}

※招待リンクはスマホ、タブレットでタップしてください。

※招待リンクには有効期限がありますので、お早めにご参加ください。

※ツイオクについてはこちら
https://redjourney.notion.site/d8f40196594a466f8cff4f5966ef7855 をご覧ください。`;
    }, [watchedReceiverNickname, lineLinkUrl]);

    const handleCopyLineLinkUrl = useCallback(() => {
        onActionClicked?.();
        if (!newAnswererInfo.answererId || !lineLinkUrl) {
            showToast("リンクを準備中です。しばらくしてからお試しください。");
            return;
        }
        if (copy(invitationMessage)) {
            showToast("リンクをコピーしました。");
        }
    }, [
        onActionClicked,
        newAnswererInfo.answererId,
        lineLinkUrl,
        invitationMessage,
    ]);

    const handleClickLineShare = useCallback(() => {
        onActionClicked?.();
        if (!newAnswererInfo.answererId || !lineLinkUrl) {
            showToast("リンクを準備中です。しばらくしてからお試しください。");
            return;
        }
        shareLineLink({ text: invitationMessage });
    }, [
        shareLineLink,
        onActionClicked,
        newAnswererInfo.answererId,
        lineLinkUrl,
        invitationMessage,
    ]);

    return (
        <>
            <p className="text-sm text-gray-500">
                以下のメッセージを親御さまのLINEに共有してください
            </p>
            <div className="my-1 max-w-full break-words whitespace-pre-wrap rounded border bg-gray-50 p-4 text-sm text-gray-700">
                {invitationMessage || (
                    <span className="text-gray-400">リンクを準備中です...</span>
                )}
            </div>
            {isMobile && (
                <Button
                    type="button"
                    className="flex h-12 w-full items-center justify-center gap-2"
                    disabled={
                        isError || !newAnswererInfo.answererId || !lineLinkUrl
                    }
                    onClick={handleClickLineShare}
                >
                    <img
                        src={LineIcon}
                        alt="LINEで共有"
                        width={24}
                        height={24}
                    />
                    LINEで共有する
                </Button>
            )}
            <Button
                type="button"
                className="flex h-12 w-full items-center justify-center gap-2"
                variant="outline"
                disabled={
                    isError || !newAnswererInfo.answererId || !lineLinkUrl
                }
                onClick={handleCopyLineLinkUrl}
            >
                <img src={ClipboardIcon} alt="リンクをコピー" />
                リンクをコピーする
            </Button>
        </>
    );
};
